import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NeoAvatarModule, INeoAvatarOptions, AVATAR_COLOR_SCHEMES } from '@neogov/pattern-library/neo-avatar';

export type AvatarSize = 'sm' | 'md' | 'lg';
export type AvatarColorScheme = typeof AVATAR_COLOR_SCHEMES[number];

/**
 * Avatar Component
 *
 * Wraps neo-avatar from the NeoGov pattern library.
 * Displays user avatars with initials or images.
 *
 * @example With firstName and lastName
 * ```html
 * <cm-avatar
 *   [firstName]="'John'"
 *   [lastName]="'Doe'"
 *   [size]="'md'"
 * />
 * ```
 *
 * @example With fullName
 * ```html
 * <cm-avatar
 *   [fullName]="'John Doe'"
 *   [size]="'lg'"
 * />
 * ```
 *
 * @example With image
 * ```html
 * <cm-avatar
 *   [src]="userImageUrl"
 *   [alt]="'John Doe'"
 *   [size]="'md'"
 * />
 * ```
 */
@Component({
  selector: 'cm-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NeoAvatarModule],
  templateUrl: './avatar.component.html',
  styleUrl: './avatar.component.less',
})
export class AvatarComponent {
  /**
   * First name (used to generate initials)
   */
  public readonly firstName = input<string>('');

  /**
   * Last name (used to generate initials)
   */
  public readonly lastName = input<string>('');

  /**
   * Full name (alternative to firstName + lastName)
   */
  public readonly fullName = input<string>('');

  /**
   * Image source URL
   */
  public readonly src = input<string>('');

  /**
   * Alt text for the image
   */
  public readonly alt = input<string>('');

  /**
   * Size of the avatar
   */
  public readonly size = input<AvatarSize>('sm');

  /**
   * Color scheme for the avatar background
   */
  public readonly colorScheme = input<AvatarColorScheme | undefined>("avatar--blackberry");

  /**
   * Automation test ID
   */
  public readonly autId = input<string>('');

  /**
   * Avatar configuration object for neo-avatar
   */
  public readonly avatarConfig = computed<INeoAvatarOptions>(() => {
    return {
      firstName: this.firstName(),
      lastName: this.lastName(),
      fullName: this.fullName(),
      src: undefined,
      alt: this.alt(),
      sizeClass: this.size(),
      colorClass: this.colorScheme(),
    };
  });
}
